import React from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography
} from '@mui/material';
import { LockOutlined, LogoutOutlined, SupportAgentOutlined } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

interface AccountRestriction {
  is_limited: boolean;
  reason?: string;
  message?: string;
  support_email?: string;
}

interface AccountRestrictionModalProps {
  restriction?: AccountRestriction;
  onLogout: () => void;
}

const AccountRestrictionModal: React.FC<AccountRestrictionModalProps> = ({
  restriction,
  onLogout
}) => {
  const { t } = useTranslation('login');
  const supportEmail = restriction?.support_email || 'contact@rintintin-booking.com';
  const message = restriction?.message || t('accountRestrictionModal.defaultMessage');
  const mailto = `mailto:${supportEmail}?subject=${encodeURIComponent(t('accountRestrictionModal.emailSubject'))}`;

  return (
    <Dialog
      open={Boolean(restriction?.is_limited)}
      onClose={() => undefined}
      disableEscapeKeyDown
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 4,
          overflow: 'hidden'
        }
      }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Box display="flex" alignItems="center" gap={1.5}>
          <LockOutlined color="warning" />
          <Typography variant="h6" fontWeight={800}>
            {t('accountRestrictionModal.title')}
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent>
        <Alert severity="warning" sx={{ mb: 2 }}>
          {message}
        </Alert>
        <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.7 }}>
          {t('accountRestrictionModal.description')}
        </Typography>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3, gap: 1, flexWrap: 'wrap' }}>
        <Button
          href={mailto}
          variant="contained"
          color="warning"
          startIcon={<SupportAgentOutlined />}
        >
          {t('accountRestrictionModal.contactSupport')}
        </Button>
        <Button
          onClick={onLogout}
          variant="outlined"
          color="inherit"
          startIcon={<LogoutOutlined />}
        >
          {t('accountRestrictionModal.logout')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AccountRestrictionModal;
